import React, { useEffect, useState } from 'react';

const OnboardingSuccessDisplay = ({ 
  title = "Success!",
  message = "Operation completed successfully",
  type = 'step', // 'step', 'completion', 'celebration'
  autoHide = false,
  hideDelay = 3000,
  onHide,
  showConfetti = false,
  className=""
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => setShowAnimation(true), 100);
    
    if (autoHide) {
      const hideTimer = setTimeout(() => {
        setIsVisible(false);
        if (onHide) onHide();
      }, hideDelay);
      
      return () => {
        clearTimeout(timer);
        clearTimeout(hideTimer);
      };
    }
    
    return () => clearTimeout(timer);
  }, [autoHide, hideDelay, onHide]);

  const getSuccessStyles = () => {
    switch (type) {
      case 'completion':
        return {
          container: 'bg-gradient-to-r from-teal-50 to-green-50 border-teal-200',
          icon: 'text-teal-600',
          title: 'text-teal-800',
          message: 'text-teal-700',
          iconSize: 'w-12 h-12'
        };
      case 'celebration':
        return {
          container: 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200',
          icon: 'text-purple-600',
          title: 'text-purple-800',
          message: 'text-purple-700',
          iconSize: 'w-16 h-16'
        };
      default: // step
        return {
          container: 'bg-green-50 border-green-200',
          icon: 'text-green-600',
          title: 'text-green-800',
          message: 'text-green-700',
          iconSize: 'w-8 h-8'
        };
    }
  };

  const styles = getSuccessStyles();

  const renderIcon = () => {
    if (type === 'celebration') {
      return (
        <div className={`${styles.icon} ${styles.iconSize} animate-bounce`}>
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }

    return (
      <div className={`${styles.icon} ${styles.iconSize} ${showAnimation ? 'animate-pulse' : ''}`}>
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      </div>
    );
  };

  if (!isVisible) return null;

  return (
    <div className={`
      rounded-lg border p-4 transition-all duration-500 ease-out
      ${styles.container}
      ${showAnimation ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}
      ${className}
    `}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {renderIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-semibold ${styles.title}`}>
            {title}
          </h3>
          <div className={`mt-1 text-sm ${styles.message}`}>
            <p>{message}</p>
          </div>
        </div>
        {!autoHide && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={() => {
                setIsVisible(false);
                if (onHide) onHide();
              }}
              className={`inline-flex rounded-md p-1.5 transition-colors ${styles.icon} hover:bg-white hover:bg-opacity-20`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
      
      {/* Confetti effect for celebrations */}
      {showConfetti && type === 'celebration' && (
        <ConfettiEffect />
      )}
    </div>
  );
};

// Simple confetti effect component
const ConfettiEffect = () => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      color: colors[Math.floor(Math.random() * colors.length)],
      left: Math.random() * 100,
      animationDelay: Math.random() * 2,
      animationDuration: 2 + Math.random() * 2
    }));
    
    setParticles(newParticles);
    
    // Clean up particles after animation
    const cleanup = setTimeout(() => setParticles([]), 4000);
    return () => clearTimeout(cleanup);
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute w-2 h-2 rounded-full animate-bounce"
          style={{
            backgroundColor: particle.color,
            left: `${particle.left}%`,
            animationDelay: `${particle.animationDelay}s`,
            animationDuration: `${particle.animationDuration}s`,
            top: '-10px'
          }}
        />
      ))}
    </div>
  );
};

// Toast-style success notification
export const OnboardingSuccessToast = ({ 
  message, 
  isVisible, 
  onHide,
  position = 'top-right' // 'top-right', 'top-left', 'bottom-right', 'bottom-left'
}) => {
  const getPositionStyles = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      default: // top-right
        return 'top-4 right-4';
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed z-50 ${getPositionStyles()}`}>
      <OnboardingSuccessDisplay
        title="Success!"
        message={message}
        type="step"
        autoHide={true}
        hideDelay={3000}
        onHide={onHide}
        className="shadow-lg max-w-sm"
      />
    </div>
  );
};

// Step completion celebration
export const StepCompletionCelebration = ({ 
  stepName, 
  isVisible, 
  onComplete,
  showNextStep = true,
  nextStepName = ""
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center dark:bg-gray-800">
        <OnboardingSuccessDisplay
          title={`${stepName} Complete!`}
          message={showNextStep && nextStepName ? `Great job! Next up: ${nextStepName}` : "Well done! Moving on to the next step."}
          type="celebration"
          showConfetti={true}
          className="mb-6"
        />
        
        <button
          onClick={onComplete}
          className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium dark:text-white"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default OnboardingSuccessDisplay;
